/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

/**
 * Wildcard for one segment of an event name, e.g. "*.create.comment".
 */
export const WSS_EVENT_WILDCARD = "*";

/**
 * Separator between the segments of an event name, e.g. "ticket.create.comment".
 */
export const WSS_EVENT_SEGMENT_SEPARATOR = ".";

/**
 * Whether the given event name is a pattern (contains a wildcard) rather than a concrete event name.
 *
 * @param eventName the event name or pattern
 */
export function isWssEventPattern(eventName: string): boolean {
  return eventName.includes(WSS_EVENT_WILDCARD);
}

/**
 * Matches an event name against a pattern.
 *
 * A pattern is an event name where any segment may be the wildcard "*", which matches exactly one segment:
 * - "*.create.comment" matches "ticket.create.comment" and "report.create.comment", but not "comment.create"
 * - "ticket.*.*" matches every event about tickets
 * - "*" on its own matches every event
 *
 * A pattern without a wildcard only matches the identical event name.
 *
 * @param pattern the pattern, e.g. "*.create.comment"
 * @param eventName the concrete event name, e.g. "ticket.create.comment"
 */
export function matchesWssEventPattern(pattern: string, eventName: string): boolean {
  if (pattern === WSS_EVENT_WILDCARD) {
    return true;
  }

  if (!isWssEventPattern(pattern)) {
    return pattern === eventName;
  }

  const patternSegments = pattern.split(WSS_EVENT_SEGMENT_SEPARATOR);
  const eventSegments = eventName.split(WSS_EVENT_SEGMENT_SEPARATOR);

  if (patternSegments.length !== eventSegments.length) {
    return false;
  }

  return patternSegments.every((segment, index) => segment === WSS_EVENT_WILDCARD || segment === eventSegments[index]);
}
